import { isDefined } from '@pepperhq/location-sdk/dist/lib/typeInference';
import { styled } from '@mui/material/styles';
import { ViewBillContentSection } from 'components/bill/ui/ViewBill/ViewBillContentSection';
import {
    IAdjustmentLocalResource,
    IOrderPaymentReadResource,
    OrderAdjustmentType,
    OrderScenario
} from 'components/order/model/Order';
import { useCurrencyString } from 'lib/useCurrencyString';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { IBasketResourceV10 } from './model/Basket';
import { Box, Typography } from '@mui/material';

import clsx from 'clsx';

const PREFIX = 'ViewBasketPaymentInfo';

const classes = {
    boldText: `${PREFIX}boldText`
};

const StyledBox = styled(Box)(({ theme }) => ({
    [`& .${classes.boldText}`]: {
        fontWeight: theme.typography.fontWeightBold
    }
}));

interface ViewBasketPaymentInfoProps {
    tip: number;
    scenario: OrderScenario;
    calculatedBasket: IBasketResourceV10 | null;
    giftCardPayment?: IOrderPaymentReadResource;
    total: number;
    selectedAwards?: IAdjustmentLocalResource[];
    multi?: boolean;
}

export const ViewBasketPaymentInfo: React.FC<React.PropsWithChildren<ViewBasketPaymentInfoProps>> = ({
    tip,
    scenario,
    calculatedBasket,
    giftCardPayment,
    total,
    selectedAwards,
    multi
}) => {
    const { t } = useTranslation();

    const getCurrencyString = useCurrencyString();
    const paymentInfoStrings = React.useMemo(() => {
        const strings: [string, string, boolean?][] = [];

        strings.push([
            t('BILL_CHECKOUT_PAYMENT_INFO_ITEMS'),
            getCurrencyString(calculatedBasket?.total.cost ?? 0)
        ]);

        if (calculatedBasket?.adjustments.length) {
            calculatedBasket?.adjustments.forEach(item => {
                if (item.type === OrderAdjustmentType.CHARGE) {
                    const value = item.quantity ? item.quantity * item.value : item.value;
                    strings.push([
                        item.description || t('BILL_CHECKOUT_PAYMENT_INFO_CHARGES'),
                        getCurrencyString(value)
                    ]);
                }
            });
        }

        if (
            (OrderScenario.PREORDER === scenario || OrderScenario.ORDER_TO_TABLE === scenario) &&
            calculatedBasket?.adjustments.length
        ) {
            calculatedBasket?.adjustments.forEach(item => {
                if (item.type === OrderAdjustmentType.DISCOUNT) {
                    const value = item.quantity ? item.quantity * item.value : item.value;
                    strings.push([
                        item.description || t('BILL_CHECKOUT_PAYMENT_INFO_DISCOUNTS'),
                        getCurrencyString(value)
                    ]);
                }
            });
        }

        if (selectedAwards && !multi) {
            selectedAwards.forEach(selectedAward => {
                strings.push([
                    selectedAward.description || t('BILL_CHECKOUT_PAYMENT_INFO_DISCOUNTS'),
                    getCurrencyString(selectedAward.totalValue)
                ]);
            });
        }

        if (OrderScenario.TABLE === scenario && calculatedBasket?.total.discounts !== 0) {
            strings.push([
                t('BILL_CHECKOUT_PAYMENT_INFO_DISCOUNTS'),
                getCurrencyString(calculatedBasket?.total.discounts ?? 0)
            ]);
        }

        if (calculatedBasket?.total.taxes.length && calculatedBasket.total.taxes.length > 0) {
            calculatedBasket.total.taxes
                .filter(tax => !tax.inclusive)
                .forEach(tax => strings.push([tax.name, getCurrencyString(tax.amount)]));
        }

        if (tip > 0) {
            strings.push([t('BILL_CHECKOUT_PAYMENT_INFO_TIP'), getCurrencyString(tip)]);
        }

        if (giftCardPayment && isDefined(giftCardPayment.amount)) {
            strings.push([t('CHECKOUT_GIFTCARD_SUMMARY'), getCurrencyString(giftCardPayment.amount)]);
        }
        strings.push([t('BILL_CHECKOUT_PAYMENT_INFO_TOTAL_TO_PAY'), getCurrencyString(total), true]);

        return strings;
    }, [
        t,
        getCurrencyString,
        calculatedBasket?.total.cost,
        calculatedBasket?.total.discounts,
        calculatedBasket?.total.taxes,
        calculatedBasket?.adjustments,
        scenario,
        selectedAwards,
        multi,
        tip,
        giftCardPayment,
        total
    ]);
    const renderInfoString = React.useCallback(
        ([key, value, bold]: [string, string, boolean?]) => (
            <StyledBox key={key} display="flex" justifyContent="space-between">
                <Typography className={clsx({ [classes.boldText]: bold })}>{key}</Typography>
                <Typography className={clsx({ [classes.boldText]: bold })}>{value}</Typography>
            </StyledBox>
        ),
        []
    );
    return (
        <ViewBillContentSection
            title={t('BILL_CHECKOUT_PAYMENT_INFO_TITLE')}
            subtitle={t('BILL_CHECKOUT_PAYMENT_INFO_BODY')}
            bottomSpacing={2}
        >
            {paymentInfoStrings.map(renderInfoString)}
        </ViewBillContentSection>
    );
};
